import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Spinner, Badge, Table } from 'react-bootstrap';

import { locationService, Country, CityWithCountry, CreateCountryRequest, UpdateCountryRequest, CreateCityRequest, UpdateCityRequest } from '../../../components/Locations/services/locationService';
import './LocationsManagement.css';
import { showConfirm, showError, showSuccess } from '../../../Utilities/sweetAlert';

const LocationsManagement: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<CityWithCountry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'countries' | 'cities'>('countries');
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);

  // Modal states
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [editingCity, setEditingCity] = useState<CityWithCountry | null>(null);

  // Form states
  const [countryForm, setCountryForm] = useState<CreateCountryRequest>({ name: '', bbox: [0, 0, 0, 0] });
  const [cityForm, setCityForm] = useState<CreateCityRequest>({ name: '', countryId: 0 });

  // Error states
  const [error, setError] = useState<string | null>(null);

  // Fetch data
  const fetchCountries = useCallback(async () => {
    try {
      const response = await locationService.getCountries();
      setCountries(response.data.countries);
    } catch (err: any) {
      console.error('Failed to fetch countries:', err);
      setError('Failed to load countries');
    }
  }, []);

  const fetchCities = useCallback(async (countryId?: number) => {
    try {
      const response = await locationService.getCities(countryId);
      setCities(response.data.cities);
    } catch (err: any) {
      console.error('Failed to fetch cities:', err);
      setError('Failed to load cities');
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchCountries();
      if (selectedTab === 'cities') {
        await fetchCities(selectedCountryId || undefined);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchCountries, fetchCities, selectedTab, selectedCountryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Country operations
  const handleCreateCountry = async () => {
    try {
      await locationService.createCountry(countryForm);
      setShowCountryModal(false);
      setCountryForm({ name: '', bbox: [0, 0, 0, 0] });
      await fetchCountries();
      showSuccess('Country created successfully!');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to create country');
    }
  };

  const handleUpdateCountry = async () => {
    if (!editingCountry) return;
    
    try {
      const updateData: UpdateCountryRequest = {
        id: editingCountry.id,
        name: countryForm.name,
        bbox: countryForm.bbox
      };
      await locationService.updateCountry(updateData);
      setShowCountryModal(false);
      setEditingCountry(null);
      setCountryForm({ name: '', bbox: [0, 0, 0, 0] });
      await fetchCountries();
      showSuccess('Country updated successfully!');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to update country');
    }
  };

  const handleDeleteCountry = async (country: Country) => {
    try {
      // Check if deletion is allowed
      const permissionCheck = await locationService.checkDeletionPermission('country', country.id);
      const { canDelete, reason, associationType } = permissionCheck.data;

      if (!canDelete) {
        showError(
          `Cannot Delete Country`,
          `${reason}\n\nYou need to remove the associated ${associationType} first.`
        );
        return;
      }

      const confirmed = await showConfirm(
        'Delete Country',
        `Are you sure you want to delete "${country.name}"? This action cannot be undone.`,
        'Delete',
        'Cancel'
      );

      if (confirmed.isConfirmed) {
        await locationService.deleteCountry({ id: country.id });
        await fetchCountries();
        showSuccess('Country deleted successfully!');
      }
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to delete country');
    }
  };

  // City operations
  const handleCreateCity = async () => {
    try {
      await locationService.createCity(cityForm);
      setShowCityModal(false);
      setCityForm({ name: '', countryId: 0 });
      await fetchCities(selectedCountryId || undefined);
      showSuccess('City created successfully!');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to create city');
    }
  };

  const handleUpdateCity = async () => {
    if (!editingCity) return;
    
    try {
      const updateData: UpdateCityRequest = {
        id: editingCity.id,
        name: cityForm.name,
        countryId: cityForm.countryId,
      };
      await locationService.updateCity(updateData);
      setShowCityModal(false);
      setEditingCity(null);
      setCityForm({ name: '', countryId: 0 });
      await fetchCities(selectedCountryId || undefined);
      showSuccess('City updated successfully!');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to update city');
    }
  };

  const handleDeleteCity = async (city: CityWithCountry) => {
    try {
      // Check if deletion is allowed
      const permissionCheck = await locationService.checkDeletionPermission('city', city.id);
      const { canDelete, reason, associationType } = permissionCheck.data;

      if (!canDelete) {
        showError(
          `Cannot Delete City`,
          `${reason}\n\nYou need to remove the associated ${associationType} first.`
        );
        return;
      }

      const confirmed = await showConfirm(
        'Delete City',
        `Are you sure you want to delete "${city.name}" from ${city.country.name}? This action cannot be undone.`,
        'Delete',
        'Cancel'
      );

      if (confirmed.isConfirmed) {
        await locationService.deleteCity({ id: city.id });
        await fetchCities(selectedCountryId || undefined);
        showSuccess('City deleted successfully!');
      }
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to delete city');
    }
  };

  // Modal handlers
  const openCreateCountryModal = () => {
    setEditingCountry(null);
    setCountryForm({ name: '', bbox: [0, 0, 0, 0] });
    setShowCountryModal(true);
  };

  const openEditCountryModal = (country: Country) => {
    setEditingCountry(country);
    setCountryForm({ name: country.name, bbox: country.bbox || [0, 0, 0, 0] });
    setShowCountryModal(true);
  };

  const openCreateCityModal = () => {
    setEditingCity(null);
    setCityForm({ name: '', countryId: selectedCountryId || (countries[0]?.id || 0) });
    setShowCityModal(true);
  };

  const openEditCityModal = (city: CityWithCountry) => {
    setEditingCity(city);
    setCityForm({ name: city.name, countryId: city.countryId });
    setShowCityModal(true);
  };

  const filteredCities = selectedCountryId 
    ? cities.filter(city => city.countryId === selectedCountryId)
    : cities;

  if (loading) {
    return (
      <Container fluid className="locations-management">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading locations...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="locations-management">
      <div className="page-header mb-4">
        <Row className="align-items-center">
          <Col>
            <h1 className="page-title">
              <i className="fas fa-globe me-2"></i>
              Locations Management
            </h1>
            <p className="text-muted">Manage countries and cities for trail locations</p>
          </Col>
        </Row>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <div className="tabs-container mb-4">
        <Button
          variant={selectedTab === 'countries' ? 'primary' : 'outline-primary'}
          onClick={() => setSelectedTab('countries')}
          className="me-2"
        >
          <i className="fas fa-globe me-1"></i>
          Countries ({countries.length})
        </Button>
        <Button
          variant={selectedTab === 'cities' ? 'primary' : 'outline-primary'}
          onClick={() => setSelectedTab('cities')}
        >
          <i className="fas fa-city me-1"></i>
          Cities ({cities.length})
        </Button>
      </div>

      {/* Countries Tab */}
      {selectedTab === 'countries' && (
        <Card className="management-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="fas fa-globe me-2"></i>
              Countries
            </h4>
            <Button variant="success" onClick={openCreateCountryModal}>
              <i className="fas fa-plus me-1"></i>
              Add Country
            </Button>
          </Card.Header>
          <Card.Body>
            {countries.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-globe text-muted" style={{ fontSize: '3rem' }}></i>
                <p className="text-muted mt-2">No countries found. Add your first country!</p>
              </div>
            ) : (
              <Table responsive hover className="locations-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {countries?.map((country) => (
                    <tr key={country.id}>
                      <td>
                        <Badge bg="primary">{country.id}</Badge>
                      </td>
                      <td>
                        <strong>{country.name}</strong>
                        {country.bbox && (
                        <div className="small text-muted">
                          bbox: [{country.bbox.join(', ')}]
                        </div>
                      )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEditCountryModal(country)}
                          className="me-2"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteCountry(country)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Cities Tab */}
      {selectedTab === 'cities' && (
        <Card className="management-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">
                <i className="fas fa-city me-2"></i>
                Cities
              </h4>
              <div className="mt-2">
                <Form.Select
                  value={selectedCountryId || ''}
                  onChange={(e) => {
                    const countryId = e.target.value ? Number(e.target.value) : null;
                    setSelectedCountryId(countryId);
                    fetchCities(countryId || undefined);
                  }}
                  style={{ width: 'auto', display: 'inline-block' }}
                >
                  <option value="">All Countries</option>
                  {countries?.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
            <Button variant="success" onClick={openCreateCityModal}>
              <i className="fas fa-plus me-1"></i>
              Add City
            </Button>
          </Card.Header>
          <Card.Body>
            {filteredCities.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-city text-muted" style={{ fontSize: '3rem' }}></i>
                <p className="text-muted mt-2">
                  {selectedCountryId 
                    ? 'No cities found in the selected country. Add your first city!'
                    : 'No cities found. Add your first city!'}
                </p>
              </div>
            ) : (
              <Table responsive hover className="locations-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Country</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCities.map((city) => (
                    <tr key={city.id}>
                      <td>
                        <Badge bg="info">{city.id}</Badge>
                      </td>
                      <td>
                        <strong>{city.name}</strong>
                      </td>
                      <td>
                        <Badge bg="secondary">{city.country.name}</Badge>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEditCityModal(city)}
                          className="me-2"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteCity(city)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Country Modal */}
      <Modal show={showCountryModal} onHide={() => setShowCountryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCountry ? 'Edit Country' : 'Add New Country'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Country Name</Form.Label>
              <Form.Control
                type="text"
                value={countryForm.name}
                onChange={(e) => setCountryForm({ ...countryForm, name: e.target.value })}
                placeholder="Enter country name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Bounding Box (bbox)
                <small className="text-muted ms-2">Format: [West, South, East, North]</small>
              </Form.Label>
              <Row>
                <Col xs={6} md={3}>
                  <Form.Label className="small">West (min longitude)</Form.Label>
                  <Form.Control
                    type="number"
                    value={countryForm.bbox[0] || ''}
                    onChange={(e) => setCountryForm({ 
                      ...countryForm, 
                      bbox: [Number(e.target.value) || 0, countryForm.bbox[1], countryForm.bbox[2], countryForm.bbox[3]]
                    })}
                    className="hide-number-spinners"
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Label className="small">South (min latitude)</Form.Label>
                  <Form.Control
                    type="number"
                    value={countryForm.bbox[1] || ''}
                    onChange={(e) => setCountryForm({ 
                      ...countryForm, 
                      bbox: [countryForm.bbox[0], Number(e.target.value) || 0, countryForm.bbox[2], countryForm.bbox[3]]
                    })}
                    className="hide-number-spinners"
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Label className="small">East (max longitude)</Form.Label>
                  <Form.Control
                    type="number"
                    value={countryForm.bbox[2] || ''}
                    onChange={(e) => setCountryForm({ 
                      ...countryForm, 
                      bbox: [countryForm.bbox[0], countryForm.bbox[1], Number(e.target.value) || 0, countryForm.bbox[3]]
                    })}
                    className="hide-number-spinners"
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Label className="small">North (max latitude)</Form.Label>
                  <Form.Control
                    type="number"
                    value={countryForm.bbox[3] || ''}
                    onChange={(e) => setCountryForm({ 
                      ...countryForm, 
                      bbox: [countryForm.bbox[0], countryForm.bbox[1], countryForm.bbox[2], Number(e.target.value) || 0]
                    })}
                    className="hide-number-spinners"
                  />
                </Col>
              </Row>
              <Form.Text className="text-muted">
                Enter coordinates as decimal numbers (e.g., 31.2357, -120.5432)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCountryModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={editingCountry ? handleUpdateCountry : handleCreateCountry}
            disabled={!countryForm.name.trim()}
          >
            {editingCountry ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* City Modal */}
      <Modal show={showCityModal} onHide={() => setShowCityModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCity ? 'Edit City' : 'Add New City'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>City Name</Form.Label>
              <Form.Control
                type="text"
                value={cityForm.name}
                onChange={(e) => setCityForm({ ...cityForm, name: e.target.value })}
                placeholder="Enter city name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Select
                value={cityForm.countryId}
                onChange={(e) => setCityForm({ ...cityForm, countryId: Number(e.target.value) })}
              >
                <option value={0}>Select a country</option>
                {countries?.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCityModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={editingCity ? handleUpdateCity : handleCreateCity}
            disabled={!cityForm.name.trim() || cityForm.countryId === 0}
          >
            {editingCity ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LocationsManagement;